import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AcceptDisputeResponse,
  BulkUpdateDisputeTagsApplicationErrors,
  BulkUpdateDisputeTagsByFilterApplicationErrors,
  BulkUpdateDisputeTagsByFilterOptions,
  BulkUpdateDisputeTagsByFilterResponse,
  BulkUpdateDisputeTagsOptions,
  BulkUpdateDisputeTagsResponse,
  DefendDisputeResponse,
  Dispute,
  DisputeCreatedEnvelope,
  DisputeQuery,
  DisputeUpdatedEnvelope,
  DisputesQueryBuilder,
  QueryDisputesResponse,
  acceptDispute as universalAcceptDispute,
  bulkUpdateDisputeTags as universalBulkUpdateDisputeTags,
  bulkUpdateDisputeTagsByFilter as universalBulkUpdateDisputeTagsByFilter,
  defendDispute as universalDefendDispute,
  getDispute as universalGetDispute,
  queryDisputes as universalQueryDisputes,
  typedQueryDisputes as universalTypedQueryDisputes,
} from './payments-disputes-v1-dispute-disputes.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/payments' };

export function getDispute(httpClient: HttpClient): GetDisputeSignature {
  return (disputeId: string) =>
    universalGetDispute(
      disputeId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetDisputeSignature {
  /**
   * Retrieves a dispute.
   * @param - ID of the dispute to retrieve.
   * @returns The retrieved dispute.
   */
  (disputeId: string): Promise<
    NonNullablePaths<
      Dispute,
      | `_id`
      | `chargeId`
      | `providerDisputeId`
      | `stage`
      | `reason`
      | `status`
      | `sellerProtection`
      | `channel`
      | `actions`
      | `actions.${number}.type`
      | `tags.privateTags.tagIds`,
      4
    >
  >;
}

export function queryDisputes(httpClient: HttpClient): QueryDisputesSignature {
  return () =>
    universalQueryDisputes(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryDisputesSignature {
  /**
   * Creates a query to retrieve a list of disputes.
   *
   * The Query Disputes method builds a query to retrieve a list of disputes and returns a `DisputesQueryBuilder` object.
   *
   * The returned object contains the query definition, which is used to run the query using the `find()` method.
   *
   * You can refine the query by chaining `DisputesQueryBuilder` methods onto the query. `DisputesQueryBuilder` methods enable you to filter, sort, and control the results that Query Disputes returns.
   *
   * Query Disputes has a default paging limit of 50, which you can override.
   *
   *  For a full description of the item object, see the object returned for the `items` property in `DisputesQueryResult`.
   */
  (): DisputesQueryBuilder;
}

export function typedQueryDisputes(
  httpClient: HttpClient
): TypedQueryDisputesSignature {
  return (query: DisputeQuery) =>
    universalTypedQueryDisputes(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryDisputesSignature {
  /** */
  (query: DisputeQuery): Promise<
    NonNullablePaths<
      QueryDisputesResponse,
      | `disputes`
      | `disputes.${number}._id`
      | `disputes.${number}.chargeId`
      | `disputes.${number}.providerDisputeId`
      | `disputes.${number}.stage`
      | `disputes.${number}.reason`
      | `disputes.${number}.status`
      | `disputes.${number}.sellerProtection`
      | `disputes.${number}.channel`,
      4
    >
  >;
}

export function acceptDispute(httpClient: HttpClient): AcceptDisputeSignature {
  return (disputeId: string) =>
    universalAcceptDispute(
      disputeId,
      // @ts-ignore
      { httpClient }
    );
}

interface AcceptDisputeSignature {
  /**
   * Accepts liability for a dispute, indicating the merchant agrees with the customer's claim.
   *
   * When a dispute is accepted, the refund is processed automatically.
   * @param - ID of the dispute to accept.
   */
  (disputeId: string): Promise<
    NonNullablePaths<
      AcceptDisputeResponse,
      | `dispute._id`
      | `dispute.chargeId`
      | `dispute.providerDisputeId`
      | `dispute.stage`
      | `dispute.reason`
      | `dispute.status`
      | `dispute.sellerProtection`
      | `dispute.channel`
      | `dispute.actions`
      | `dispute.actions.${number}.type`
      | `dispute.tags.privateTags.tagIds`,
      5
    >
  >;
}

export function defendDispute(httpClient: HttpClient): DefendDisputeSignature {
  return (disputeId: string) =>
    universalDefendDispute(
      disputeId,
      // @ts-ignore
      { httpClient }
    );
}

interface DefendDisputeSignature {
  /**
   * Defends a dispute by submitting evidence to the payment service provider or bank handling the dispute.
   *
   * To upload evidence documents, use the [Dispute Evidence Documents API](https://dev.wix.com/docs/api-reference/business-management/payments/disputes/dispute-evidence-documents/create-dispute-evidence-document). This method submits all the evidence documents uploaded for the specified dispute.
   * @param - ID of the dispute to defend.
   */
  (disputeId: string): Promise<
    NonNullablePaths<
      DefendDisputeResponse,
      | `dispute._id`
      | `dispute.chargeId`
      | `dispute.providerDisputeId`
      | `dispute.stage`
      | `dispute.reason`
      | `dispute.status`
      | `dispute.sellerProtection`
      | `dispute.channel`
      | `dispute.actions`
      | `dispute.actions.${number}.type`
      | `dispute.tags.privateTags.tagIds`,
      5
    >
  >;
}

export function bulkUpdateDisputeTags(
  httpClient: HttpClient
): BulkUpdateDisputeTagsSignature {
  return (ids: string[], options?: BulkUpdateDisputeTagsOptions) =>
    universalBulkUpdateDisputeTags(
      ids,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateDisputeTagsSignature {
  /**
   * Updates tags on multiple disputes simultaneously using a list of dispute IDs.
   * If a tag appears in both assign and unassign lists, it will be assigned.
   * @param - List of dispute IDs to update tags for.
   */
  (ids: string[], options?: BulkUpdateDisputeTagsOptions): Promise<
    NonNullablePaths<
      BulkUpdateDisputeTagsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    > & {
      __applicationErrorsType?: BulkUpdateDisputeTagsApplicationErrors;
    }
  >;
}

export function bulkUpdateDisputeTagsByFilter(
  httpClient: HttpClient
): BulkUpdateDisputeTagsByFilterSignature {
  return (
    filter: Record<string, any>,
    options?: BulkUpdateDisputeTagsByFilterOptions
  ) =>
    universalBulkUpdateDisputeTagsByFilter(
      filter,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateDisputeTagsByFilterSignature {
  /**
   * Asynchronously updates tags on multiple disputes based on filter criteria.
   * Returns a job ID that can be used to track the operation status. If a tag appears in both assign and unassign lists, it will be assigned.
   * @param - Filter that determines which disputes to update tags for.
   *
   * An empty filter updates all disputes.
   */
  (
    filter: Record<string, any>,
    options?: BulkUpdateDisputeTagsByFilterOptions
  ): Promise<
    NonNullablePaths<BulkUpdateDisputeTagsByFilterResponse, `jobId`, 2> & {
      __applicationErrorsType?: BulkUpdateDisputeTagsByFilterApplicationErrors;
    }
  >;
}

export const onDisputeCreated = EventDefinition(
  'wix.payments.disputes.v1.dispute_created',
  true,
  (event: DisputeCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.dueDate' },
            { path: 'entity.defendedDate' },
            { path: 'entity.acceptedDate' },
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.actions.dueDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<DisputeCreatedEnvelope>();
export const onDisputeUpdated = EventDefinition(
  'wix.payments.disputes.v1.dispute_updated',
  true,
  (event: DisputeUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.dueDate' },
            { path: 'entity.defendedDate' },
            { path: 'entity.acceptedDate' },
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.actions.dueDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.dueDate' },
            { path: 'modifiedFields.defendedDate' },
            { path: 'modifiedFields.acceptedDate' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
            { path: 'modifiedFields.actions.dueDate' },
          ],
        },
      ])
    )
)<DisputeUpdatedEnvelope>();

export {
  AcceptDisputeRequest,
  AcceptDisputeResponse,
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  ApplicationError,
  BaseEventMetadata,
  BulkActionMetadata,
  BulkUpdateDisputeTagsByFilterOptions,
  BulkUpdateDisputeTagsByFilterRequest,
  BulkUpdateDisputeTagsByFilterResponse,
  BulkUpdateDisputeTagsOptions,
  BulkUpdateDisputeTagsRequest,
  BulkUpdateDisputeTagsResponse,
  BulkUpdateDisputeTagsResult,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DefendDisputeRequest,
  DefendDisputeResponse,
  Dispute,
  DisputeAction,
  DisputeActionType,
  DisputeChannel,
  DisputeCreatedEnvelope,
  DisputeQuerySpec,
  DisputeReason,
  DisputeStage,
  DisputeStatus,
  DisputeUpdatedEnvelope,
  DisputesQueryBuilder,
  DisputesQueryResult,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  ExtendedFields,
  GetDisputeRequest,
  GetDisputeResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ItemMetadata,
  MessageEnvelope,
  QueryDisputesRequest,
  QueryDisputesResponse,
  RestoreInfo,
  SellerProtection,
  SortOrder,
  Sorting,
  TagList,
  Tags,
  TagsModified,
  WebhookIdentityType,
  utils,
} from './payments-disputes-v1-dispute-disputes.universal.js';
